import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import queryString from 'query-string';
import { useDispatch } from 'react-redux';
import { i18nKeys, useTranslate } from '@gloat/glocalize';
import { CareerPath } from '../types';
import { notificationSendDismiss } from '../../../../../../redux/notifications/notifications-actions';
import { apiAuthClient } from '../../../../../../root/api-auth-client';

export const CAREER_PATH_KEY = 'careerPath';
export const CAREER_PATH_LANES_KEY = 'feedPaths';
export const CAREER_PATH_SEARCH_LANES_KEY = 'searchPaths';
type QueryOptions = UseQueryOptions<CareerPath[], Error>;
export type TPathsPayload = { lanes: CareerPath[] };

export const usePaths = ({
  select,
}: {
  select?: UseQueryOptions<CareerPath[]>['select'];
} = {}) => {
  return useQuery<CareerPath[]>({
    queryKey: [CAREER_PATH_KEY, CAREER_PATH_LANES_KEY],

    queryFn: async () => {
      const res = await apiAuthClient.fetch(`/api/v1/career-path/lanes/suggestions`);

      if (!res.ok) {
        throw new Error(`Failed to retrieve paths`);
      }

      const data: TPathsPayload = await res.json();
      return data.lanes;
    },
    select,
  });
};

export const useSearchPaths = ({
  query,
  options,
}: {
  query?: Record<any, any>;
  options?: Omit<QueryOptions, 'queryKey' | 'queryFn'>;
}): UseQueryResult<CareerPath[], Error> => {
  return useQuery<CareerPath[], Error>({
    queryKey: [CAREER_PATH_KEY, CAREER_PATH_SEARCH_LANES_KEY],
    queryFn: async () => {
      const queryAsString = queryString.stringify(query || {});
      const res = await apiAuthClient.fetch(`/api/v1/career-path/lanes/suggestions?${queryAsString}`);

      if (!res.ok) {
        throw new Error(`Failed to retrieve searched paths`);
      }

      const data: TPathsPayload = await res.json();
      return data.lanes;
    },
    ...options,
    staleTime: 0,
    gcTime: 0,
  });
};

export const useUnsavedPaths = () => {
  return usePaths({
    select: data => {
      return (data || []).filter((path: CareerPath) => !path.isSaved);
    },
  });
};

export const useSavedPaths = () => {
  return usePaths({
    select: data => {
      return (data || []).filter((path: CareerPath) => path.isSaved);
    },
  });
};

export const useUpdatePathSaveStatus = (): UseMutationResult<CareerPath, Error, { path: CareerPath }> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch<any>();
  const translate = useTranslate();

  return useMutation({
    mutationFn: async ({ path }: { path: CareerPath }) => {
      const action = path.isSaved ? 'unsave' : 'save';

      const res = await apiAuthClient.fetch(`/api/v1/career-path/lanes/${action}`, {
        method: 'POST',
        body: JSON.stringify(path),
      });

      const data: CareerPath = await res.json();
      return data;
    },

    onSuccess: (payload, { path }) => {
      dispatch(
        notificationSendDismiss({
          message: translate({
            id: path.isSaved
              ? i18nKeys.CAREER_PATH_UNSAVED_PATH_SUCCESSFULLY
              : i18nKeys.CAREER_PATH_SAVED_PATH_SUCCESSFULLY,
          }),
          dismissAfter: 5000,
        }),
      );
      queryClient.setQueriesData({ queryKey: [CAREER_PATH_KEY] }, (prevData: any) => {
        if (!prevData) {
          return [];
        }
        return prevData.map((prevPath: CareerPath) => {
          if (prevPath.hash === path.hash) {
            return { ...prevPath, isSaved: !path.isSaved, id: payload?.id || null };
          }
          return prevPath;
        });
      });
      if (path.isSaved) {
        queryClient.setQueriesData({ queryKey: [CAREER_PATH_KEY] }, (prevData: any) => {
          if (!prevData) {
            return [];
          }
          const isPathExistsInFeed = !!prevData.find((prevPath: CareerPath) => path.hash === prevPath.hash);
          if (!isPathExistsInFeed) {
            return [path, ...prevData];
          } else {
            return prevData;
          }
        });
      }
      queryClient.invalidateQueries({ queryKey: [CAREER_PATH_KEY, CAREER_PATH_LANES_KEY] });
    },
  });
};
